import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import client, { API } from "@/lib/api";
import { PageHeader, EmptyState, Skeletons, ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Images, ArrowLeft, ArrowRight, Loader2, MapPin, Trash2, Pencil, Link2, Check, ImagePlus, FileDown, Search, Folder, ChevronRight } from "lucide-react";

export const mediaThumb = (id) => `${API}/public/portfolio/media/${id}?size=thumb`;
export const mediaFull = (id) => `${API}/public/portfolio/media/${id}`;

export async function downloadPdf(url, filename, setBusy) {
  setBusy?.(true);
  try {
    const res = await client.get(url, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(res.data);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  } catch (e) { toast.error(e?.response?.status === 404 ? "Nothing to export yet" : "Couldn't generate PDF"); } finally { setBusy?.(false); }
}

// Shared share-link control used for both categories and projects.
export function ShareRow({ kind, id, enabled, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const base = kind === "category" ? `/portfolio/categories/${id}/share` : `/portfolio/projects/${id}/share`;
  const url = token ? `${window.location.origin}/showcase/${kind === "category" ? "c" : "p"}/${token}` : "";
  const toggle = async (next) => {
    setBusy(true);
    try {
      const { data } = await client.post(base, { enabled: next });
      onChanged?.(data);
      toast.success(next ? "Share link is live" : "Share link turned off");
    } catch { toast.error("Couldn't update sharing"); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`share-row-${kind}-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-ink">Public share link</div>
          <div className="text-xs text-muted-foreground">Anyone with the link can view this {kind}. No login needed.</div>
        </div>
        <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} data-testid={`share-toggle-${kind}`} />
      </div>
      {enabled && token && (
        <div className="mt-3 flex items-center gap-2">
          <Input readOnly value={url} className="h-9 text-xs" data-testid={`share-url-${kind}`} onFocus={(e) => e.target.select()} />
          <Button size="sm" variant="outline" onClick={copy} className="h-9 shrink-0 gap-1.5" data-testid={`share-copy-${kind}`}>
            {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Link2 className="h-3.5 w-3.5" />}Copy
          </Button>
        </div>
      )}
    </div>
  );
}

function CategoryFormDialog({ category = null, parentId = null, onSaved, trigger, triggerTestId = "add-category-btn" }) {
  const isEdit = !!category;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const onOpenChange = (v) => {
    setOpen(v);
    if (v) setForm(isEdit ? { name: category.name || "", description: category.description || "" } : { name: "", description: "" });
  };
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Category name is required"); return; }
    setSaving(true);
    try {
      const { data } = isEdit ? await client.put(`/portfolio/categories/${category.id}`, form) : await client.post("/portfolio/categories", { ...form, parent_id: parentId || null });
      toast.success(isEdit ? "Category updated" : "Category added");
      setOpen(false); onSaved?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save category"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger || <Button className="gap-2" data-testid={triggerTestId}><Plus className="h-4 w-4" />New category</Button>}</DialogTrigger>
      <DialogContent data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{isEdit ? "Edit category" : "New category"}</DialogTitle>
          <DialogDescription>Group your finished work, e.g. Alfresco Tiling, Bathroom Renovations.</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5"><Label>Category name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Bathroom Renovations" data-testid="category-name-input" /></div>
          <div className="grid gap-1.5"><Label>Short description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="A line about this style of work" data-testid="category-desc-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="category-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isEdit ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["portfolio-categories", "root"], queryFn: async () => (await client.get("/portfolio/categories")).data });
  const searchQ = useQuery({ queryKey: ["portfolio-search", q], queryFn: async () => (await client.get("/portfolio/search", { params: { q: q.trim() } })).data, enabled: q.trim().length > 0, staleTime: 10000 });
  const searching = q.trim().length > 0;
  const cats = data || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["portfolio-categories", "root"] });
  return (
    <div className="animate-fade-up">
      <PageHeader title="Portfolio" subtitle="Showcase your finished work — share a whole category or a single project with customers" testid="portfolio-header">
        <CategoryFormDialog onSaved={refresh} />
      </PageHeader>
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories & projects…" className="pl-9" data-testid="portfolio-search" />
      </div>
      {searching ? (
        <div className="space-y-2" data-testid="portfolio-search-results">
          {searchQ.isLoading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
            : (!searchQ.data?.categories?.length && !searchQ.data?.projects?.length) ? <p className="rounded-2xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground">No matches for “{q}”.</p>
            : <>
              {(searchQ.data.categories || []).map((c) => (
                <button key={c.id} onClick={() => navigate(`/portfolio/${c.id}`)} data-testid={`search-cat-${c.id}`} className="flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-white p-3 text-left hover:border-primary/20">
                  <Folder className="h-4 w-4 shrink-0 text-primary" /><span className="font-medium text-ink">{c.name}</span>{c.path?.length ? <span className="truncate text-xs text-muted-foreground">{c.path.join(" › ")}</span> : null}
                </button>
              ))}
              {(searchQ.data.projects || []).map((p) => (
                <button key={p.id} onClick={() => navigate(`/portfolio/projects/${p.id}`)} data-testid={`search-proj-${p.id}`} className="flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-white p-3 text-left hover:border-primary/20">
                  <Images className="h-4 w-4 shrink-0 text-slate-400" /><span className="font-medium text-ink">{p.title}</span><span className="truncate text-xs text-muted-foreground">{(p.path || []).join(" › ")}</span>
                </button>
              ))}
            </>}
        </div>
      ) : isError ? <ErrorState onRetry={refetch} message="We couldn't load your portfolio." />
        : isLoading ? <Skeletons />
        : cats.length === 0 ? <EmptyState icon={Images} title="No categories yet" hint="Create a category like Bathroom Renovations to start adding projects." action={<CategoryFormDialog triggerTestId="empty-add-category-btn" onSaved={refresh} />} />
        : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="portfolio-category-grid">
            {cats.map((c) => (
              <button key={c.id} onClick={() => navigate(`/portfolio/${c.id}`)} data-testid={`portfolio-category-${c.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_8px_24px_rgba(35,52,90,0.08)]">
                <div className="aspect-[16/10] w-full overflow-hidden bg-slate-100">
                  {c.cover_media_id
                    ? <img src={mediaThumb(c.cover_media_id)} alt={c.name} className="h-full w-full object-contain transition-transform group-hover:scale-105" />
                    : <div className="grid h-full w-full place-items-center text-slate-300"><Images className="h-8 w-8" /></div>}
                </div>
                <div className="flex flex-1 items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <h3 className="truncate font-display text-base font-bold text-ink">{c.name}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">{c.subcategory_count > 0 ? `${c.subcategory_count} sub · ` : ""}{c.project_count} {c.project_count === 1 ? "project" : "projects"}{c.share_enabled ? " · shared" : ""}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

function ProjectFormDialog({ categoryId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", location: "", description: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Project title is required"); return; }
    setSaving(true);
    try {
      const { data } = await client.post(`/portfolio/categories/${categoryId}/projects`, form);
      toast.success("Project created"); setForm({ title: "", location: "", description: "" }); setOpen(false); onSaved?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create project"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="add-project-btn"><Plus className="h-4 w-4" />New project</Button></DialogTrigger>
      <DialogContent data-testid="project-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">New project</DialogTitle><DialogDescription>One completed job you want to showcase.</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Modern Ensuite — Point Cook" data-testid="project-title-input" /></div>
          <div className="grid gap-1.5"><Label>Location / suburb (optional)</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="project-location-input" /></div>
          <div className="grid gap-1.5"><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="project-desc-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="project-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDetail({ categoryId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const coverRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["portfolio-category", categoryId], queryFn: async () => (await client.get(`/portfolio/categories/${categoryId}`)).data });
  const category = data;
  const subs = data?.subcategories || [];
  const projects = data?.projects || [];
  const refreshCats = () => { qc.invalidateQueries({ queryKey: ["portfolio-category", categoryId] }); qc.invalidateQueries({ queryKey: ["portfolio-categories", "root"] }); };
  const refreshProjects = refreshCats;

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try { await client.post(`/portfolio/categories/${categoryId}/cover`, (() => { const fd = new FormData(); fd.append("file", file); return fd; })(), { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Cover updated"); refreshCats(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't upload cover"); } finally { setUploadingCover(false); }
  };
  const removeCover = async () => {
    if (!window.confirm("Remove the cover image?")) return;
    try { await client.delete(`/portfolio/categories/${categoryId}/cover`); toast.success("Cover removed"); refreshCats(); }
    catch { toast.error("Couldn't remove cover"); }
  };
  const delCategory = async () => {
    const extra = [category?.subcategory_count ? `${category.subcategory_count} sub-categor${category.subcategory_count === 1 ? "y" : "ies"}` : null, category?.project_count ? `${category.project_count} project${category.project_count === 1 ? "" : "s"}` : null].filter(Boolean).join(" and ");
    if (!window.confirm(`Delete "${category?.name}"${extra ? ` and its ${extra}` : ""}? This can't be undone.`)) return;
    try { await client.delete(`/portfolio/categories/${categoryId}`); toast.success("Category deleted"); const path = category.path || []; const parent = path.length >= 2 ? path[path.length - 2].id : null; qc.invalidateQueries({ queryKey: ["portfolio-categories", "root"] }); navigate(parent ? `/portfolio/${parent}` : "/portfolio"); }
    catch { toast.error("Couldn't delete category"); }
  };

  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (!category) return <div className="py-20 text-center text-muted-foreground">Category not found.</div>;

  return (
    <div className="animate-fade-up">
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-muted-foreground" data-testid="portfolio-breadcrumbs">
        <button onClick={() => navigate("/portfolio")} className="hover:text-ink">Portfolio</button>
        {(category.path || []).map((c, i) => (
          <span key={c.id} className="flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5" />
            {i === category.path.length - 1 ? <span className="font-medium text-ink">{c.name}</span> : <button onClick={() => navigate(`/portfolio/${c.id}`)} className="hover:text-ink">{c.name}</button>}
          </span>
        ))}
      </nav>

      <div className="mb-6 grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-extrabold text-ink" data-testid="portfolio-category-name">{category.name}</h1>
              {category.description && <p className="mt-1 text-sm text-muted-foreground">{category.description}</p>}
              <p className="mt-2 text-xs text-muted-foreground">{projects.length} {projects.length === 1 ? "project" : "projects"}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadPdf(`/portfolio/categories/${categoryId}/pdf`, `TilesMate-${category.name}.pdf`, setPdfBusy)} disabled={pdfBusy} data-testid="category-pdf-btn" className="gap-1.5">{pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}PDF</Button>
              <CategoryFormDialog category={category} onSaved={refreshCats} trigger={<Button variant="outline" size="sm" className="gap-1.5" data-testid="edit-category-btn"><Pencil className="h-3.5 w-3.5" />Edit</Button>} />
              <Button variant="outline" size="sm" onClick={delCategory} data-testid="delete-category-btn" className="gap-1.5 text-error hover:text-error"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="cover-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(f); e.target.value = ""; }} />
            <Button variant="outline" size="sm" onClick={() => coverRef.current?.click()} disabled={uploadingCover} className="gap-1.5" data-testid="set-cover-btn">{uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}{category.cover_media_id ? "Change cover" : "Add cover image"}</Button>
            {category.cover_media_id && (
              <div className="flex items-center gap-2">
                <img src={mediaThumb(category.cover_media_id)} alt="cover" className="h-10 w-16 rounded-md border border-slate-200 bg-slate-50 object-contain" />
                <button type="button" onClick={removeCover} data-testid="remove-cover-btn" className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error" title="Remove cover"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        </div>
        <ShareRow kind="category" id={categoryId} enabled={category.share_enabled} token={category.share_token} onChanged={refreshCats} />
      </div>

      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-base font-bold text-ink">Sub-categories</h2>
          <CategoryFormDialog parentId={categoryId} onSaved={refreshCats} trigger={<Button variant="outline" size="sm" className="gap-1.5" data-testid="add-subcategory-btn"><Plus className="h-3.5 w-3.5" />New sub-category</Button>} />
        </div>
        {subs.length === 0 ? <p className="rounded-xl border border-dashed border-border bg-white/60 p-4 text-center text-xs text-muted-foreground">No sub-categories. Use these to nest, e.g. Tiles › 600x1200 › projects.</p>
          : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="portfolio-subcategory-grid">
            {subs.map((c) => (
              <button key={c.id} onClick={() => navigate(`/portfolio/${c.id}`)} data-testid={`portfolio-category-${c.id}`} className="group flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20">
                <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-primary/10 text-primary">{c.cover_media_id ? <img src={mediaThumb(c.cover_media_id)} alt="" className="h-full w-full object-cover" /> : <Folder className="h-5 w-5" />}</div>
                <div className="min-w-0"><div className="truncate font-display text-sm font-bold text-ink">{c.name}</div><div className="text-xs text-muted-foreground">{c.subcategory_count > 0 ? `${c.subcategory_count} sub · ` : ""}{c.project_count} {c.project_count === 1 ? "project" : "projects"}</div></div>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ))}
          </div>}
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-base font-bold text-ink">Projects</h2>
        <ProjectFormDialog categoryId={categoryId} onSaved={refreshProjects} />
      </div>
      {projects.length === 0 ? (
        <EmptyState icon={Images} title="No projects yet" hint="Add your first completed job to this category." action={<ProjectFormDialog categoryId={categoryId} onSaved={refreshProjects} />} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="portfolio-project-grid">
          {projects.map((p) => {
            const cover = p.images?.[0]?.media_id || p.before_after?.[0]?.after_media_id;
            return (
              <button key={p.id} onClick={() => navigate(`/portfolio/projects/${p.id}`)} data-testid={`portfolio-project-${p.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_8px_24px_rgba(35,52,90,0.08)]">
                <div className="aspect-[16/10] w-full overflow-hidden bg-slate-100">
                  {cover ? <img src={mediaThumb(cover)} alt={p.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    : <div className="grid h-full w-full place-items-center text-slate-300"><Images className="h-7 w-7" /></div>}
                </div>
                <div className="p-4">
                  <h3 className="truncate font-display text-[15px] font-bold text-ink">{p.title}</h3>
                  {p.location && <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{p.location}</p>}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{(p.images?.length || 0)} photos</span>
                    {(p.before_after?.length || 0) > 0 && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{p.before_after.length} before/after</span>}
                    {p.share_enabled && <span className="rounded-full bg-[#059669]/10 px-2 py-0.5 text-[11px] font-medium text-[#059669]">Shared</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Portfolio() {
  const { categoryId } = useParams();
  return categoryId ? <CategoryDetail categoryId={categoryId} /> : <CategoryList />;
}
